import { Checkbox, Drawer, Grid, MultiSelect, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { useDebouncedState } from '@mantine/hooks';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useEffect, useState } from 'react';
import { filter, prop, uniqueBy } from 'remeda';
import { useLoadLightDebtors } from 'shared/hooks/use-load-light-debtors';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { REMINDER_SENDING_MEDIA, REMINDER_TYPES } from 'types';

import type { RemindersFilter } from '../pages';

interface FilterDrawerProps {
  isOpen: boolean;
  close: () => void;
  form: UseFormReturnType<RemindersFilter>;
}

export const FilterDrawer = ({ isOpen, close, form }: FilterDrawerProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const profile = useProfile();
  const [debtorSearchText, setDebtorSearchText] = useDebouncedState('', 200);
  const [userSearchText, setUserSearchText] = useDebouncedState('', 200);
  const { lightDebtors } = useLoadLightDebtors({ query: debtorSearchText });
  const shouldLoadUsers = profile.isAdmin || profile.roles.includes('all_invoices');
  const { users = [] } = useLoadLightUsers(
    false,
    { query: userSearchText },
    { isEnabled: shouldLoadUsers },
  );
  const [persistedDebtors, setPersistedDebtors] = useState<
    Array<{
      value: string;
      label: string;
    }>
  >([]);
  const [persistedUsers, setPersistedUsers] = useState<
    Array<{
      value: string;
      label: string;
    }>
  >([]);

  const reminderTypes = REMINDER_TYPES.filter(
    type => company.package.can_use_ai_calls || type !== 'initiate_ai_call',
  ).map(type => ({
    value: type,
    label: t(tKeysLoose.REMINDER_LIST.FILTERS.REMINDER_TYPES[type.toUpperCase()]),
  }));

  const reminderSendingMedia = company.package.can_use_ai_calls
    ? REMINDER_SENDING_MEDIA
    : filter(REMINDER_SENDING_MEDIA, medium => medium !== 'ai_call');

  useEffect(() => {
    const formattedForSelect = (lightDebtors ?? []).map(debtor => ({
      value: String(debtor.id),
      label: debtor.fullName,
    }));
    setPersistedDebtors(uniqueBy([...persistedDebtors, ...formattedForSelect!], prop('value')));
  }, [lightDebtors?.map(debtor => debtor.id).join()]);

  useEffect(() => {
    const formattedForSelect = (users ?? []).map(user => ({
      value: String(user.id),
      label: user.name.full,
    }));
    setPersistedUsers(uniqueBy([...persistedUsers, ...formattedForSelect!], prop('value')));
  }, [users?.map(user => user.id).join()]);

  return (
    <Drawer
      position="right"
      opened={isOpen}
      onClose={close}
      title={t(i18nKeys.REMINDER_LIST.FILTERS.TITLE)}
    >
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.INVOICE_REFERENCE)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.INVOICE_REFERENCE)}
            {...form.getInputProps('reference')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <MultiSelect
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.DEBTOR_REFERENCE)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.DEBTOR_REFERENCE)}
            data={persistedDebtors}
            searchable
            clearable
            onSearchChange={setDebtorSearchText}
            {...form.getInputProps('debtorIds')}
          />
        </Grid.Col>
        {profile.isAdmin && (
          <Grid.Col span={12}>
            <MultiSelect
              label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.ACCOUNT_MANAGER_REFERENCE)}
              placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.ACCOUNT_MANAGER_REFERENCE)}
              data={persistedUsers}
              searchable
              clearable
              onSearchChange={setUserSearchText}
              {...form.getInputProps('accountManagerIds')}
            />
          </Grid.Col>
        )}
        <Grid.Col span={12}>
          <MultiSelect
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.REMINDER_TYPE)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.REMINDER_TYPE)}
            searchable
            clearable
            data={reminderTypes}
            {...form.getInputProps('reminderTypes')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <MultiSelect
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_MEDIA)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.SENT_MEDIA)}
            searchable
            clearable
            data={reminderSendingMedia
              .filter(medium => medium !== 'missing_information')
              .map(medium => ({
                value: medium,
                label: t((i18nKeys.REMINDER_LIST.BADGE as any)[medium.toUpperCase()]),
              }))}
            {...form.getInputProps('sentMedia')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_AFTER)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.SENT_AFTER)}
            flex={1}
            allowDeselect
            clearable
            {...form.getInputProps('startDate')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_BEFORE)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.PLACEHOLDERS.SENT_BEFORE)}
            flex={1}
            allowDeselect
            clearable
            {...form.getInputProps('endDate')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Checkbox
            {...form.getInputProps('showUndelivered', { type: 'checkbox' })}
            onChange={event =>
              form.setFieldValue('showUndelivered', event.currentTarget.checked ? true : null)
            }
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SHOW_UNDELIVERED)}
          />
        </Grid.Col>
      </Grid>
    </Drawer>
  );
};
